import { billingApi } from '@pukka/api';
import { numberUtils } from '@pukka/common/utils';
import { InformationBox, Text } from '@pukka/ui-components';
import { differenceInCalendarDays } from 'date-fns';

interface BillingDiscountReminderProps {
  readonly isBanner?: boolean;
  readonly onClose?: () => void;
}

interface BillingRecurringReminderContentProps {
  readonly finalAmount: string;
  readonly interval: string;
  readonly daysDiff: number;
}

function BillingRecurringReminderContent({ finalAmount, interval, daysDiff }: BillingRecurringReminderContentProps) {
  return (
    <>
      Your next payment of{' '}
      <strong>
        Rp {finalAmount}/{interval} will be deducted in {daysDiff} days from your card
      </strong>
      . Please make sure the remaining saldo is available
    </>
  );
}

function BillingRecurringReminder({ isBanner, onClose }: BillingDiscountReminderProps) {
  const { data: activeRecurring } = billingApi.useActiveRecurringPayment();

  const nextBillingDate = activeRecurring?.next_billing_date;
  const finalAmount = numberUtils.formatCurrency(activeRecurring?.final_amount);
  const interval = activeRecurring?.month === 1 ? 'month' : 'year';
  const daysDiff = nextBillingDate ? differenceInCalendarDays(new Date(nextBillingDate), new Date()) : 0;

  if (daysDiff > 7 || daysDiff <= 0) {
    return null;
  }

  return isBanner ? (
    <div role="alert" data-testid="billing-recurring-reminder" className="z-20 bg-yellow-100 py-3 pl-4 pr-8 text-xs md:px-14">
      <button
        type="button"
        data-testid="close-billing-recurring-banner"
        onClick={onClose}
        className="absolute right-4 top-1 border-none bg-inherit p-0 text-lg md:right-12"
      >
        &times;
      </button>
      <BillingRecurringReminderContent finalAmount={finalAmount} interval={interval} daysDiff={daysDiff} />.{' '}
      <Text as="a" weight="bold" href="/v2/settings/billing" className="border-none bg-inherit p-0 underline">
        Click here to learn more
      </Text>
    </div>
  ) : (
    <InformationBox variant="warning" className="view-transition-[recurring-reminder]">
      <BillingRecurringReminderContent finalAmount={finalAmount} interval={interval} daysDiff={daysDiff} />
    </InformationBox>
  );
}

export { BillingRecurringReminder, BillingRecurringReminderContent };
